import { useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { AlertTriangle, Pencil, XCircle } from 'lucide-react'
import {
  useCancelReview,
  useDesignatedReviewMutationPending,
  useDesignatedReviews,
} from '@/hooks/coaching/useDesignatedReview'
import type { DesignatedReviewView } from '@/types/coaching'
import { ReviewPlacementDialog } from './ReviewPlacementDialog'

interface Props {
  learnerId: number
}

export function ReviewChips({ learnerId }: Props) {
  const { t } = useTranslation('coaching')
  const { data: reviews } = useDesignatedReviews(learnerId)
  const items = reviews ?? []
  if (items.length === 0) return null

  return (
    <section
      data-testid="review-chips"
      className="mt-3"
      aria-label={t('designatedReview.cards.label')}
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {t('designatedReview.cards.label')}
      </p>
      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        {items.map((reservation) => (
          <ReviewReservationCard
            key={reservation.reviewLessonId}
            learnerId={learnerId}
            reservation={reservation}
          />
        ))}
      </div>
    </section>
  )
}

export function ReviewReservationCard({
  learnerId,
  reservation,
  onCancelled,
  showActions = false,
}: {
  learnerId: number
  reservation: DesignatedReviewView
  onCancelled?: () => void
  showActions?: boolean
}) {
  const { t } = useTranslation('coaching')
  const cancel = useCancelReview(learnerId, reservation.reviewLessonId)
  const mutationPending = useDesignatedReviewMutationPending(learnerId, reservation.reviewLessonId)
  const [moveOpen, setMoveOpen] = useState(false)
  const warning = reservation.placementStatus === 'OVERDUE'
  const completed = reservation.placementStatus === 'COMPLETED'
  const statusLabel = t(`designatedReview.cards.status.${reservation.placementStatus}`)

  return (
    <article
      className={clsx(
        'rounded-lg border p-3',
        warning
          ? 'border-amber-300 bg-amber-50'
          : completed ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 bg-white',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900"><ContentMetadataText text={reservation.lessonTitle} /></p>
          <p className="mt-0.5 text-xs text-gray-600">
            {reservation.anchorLessonTitle
              ? t('designatedReview.cards.beforeAnchor', { anchor: reservation.anchorLessonTitle })
              : t('designatedReview.cards.legacyPlacement')}
          </p>
        </div>
        <span className={clsx(
          'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
          warning
            ? 'bg-amber-100 text-amber-800'
            : completed ? 'bg-emerald-100 text-emerald-800' : 'bg-primary-50 text-primary-700',
        )}>
          {warning ? <AlertTriangle className="size-3" /> : null}
          {statusLabel}
        </span>
      </div>

      {showActions && (reservation.canMove || reservation.canCancel) && (
        <div className="mt-3 flex items-center justify-end gap-2">
          {reservation.canMove && (
            <button
              type="button"
              onClick={() => setMoveOpen(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-primary-700"
            >
              <Pencil className="size-3.5" />
              {t('designatedReview.cards.move')}
            </button>
          )}
          {reservation.canCancel && (
            <button
              type="button"
              disabled={cancel.isPending || mutationPending}
              onClick={() => cancel.mutate(undefined, { onSuccess: onCancelled })}
              className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-red-600 disabled:opacity-50"
            >
              <XCircle className="size-3.5" />
              {t('designatedReview.cards.cancel')}
            </button>
          )}
        </div>
      )}

      {moveOpen && (
        <ReviewPlacementDialog
          learnerId={learnerId}
          reviewLessonId={reservation.reviewLessonId}
          reviewLessonTitle={reservation.lessonTitle}
          initialAnchorLessonId={reservation.anchorLessonId}
          onClose={() => setMoveOpen(false)}
        />
      )}
    </article>
  )
}
